/*
================================================================================
|                                                                              |
|  FILE: src/components/common/Navbar.js                                       |
|  PURPOSE: The main navigation bar component for the entire site.             |
|  NOTE: Mobile view now has a persistent white background.                    |
|                                                                              |
================================================================================
*/
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MenuIcon, CloseIcon } from '../icons/SvgIcons';

// --- Navigation Data Structure with Submenus ---
const serviceSubLinks = [
    { name: "Environmental Recovery", to: "/services/environmental" },
    { name: "Oil & Gas Solutions", to: "/services/oil-gas" },
    { name: "Mining and Logistics", to: "/services/mining" },
];

const impactSubLinks = [
    { name: "EcoRevive", to: "/impact/ecorevive" },
];

const NAV_LINKS = [
  { name: "Home", to: "/" },
  { name: "Services", to: "/services", subLinks: serviceSubLinks },
  { name: "About Us", to: "/about" },
  { name: "Impact", to: "/impact", subLinks: impactSubLinks },
  { name: "News", to: "/news" },
  { name: "Contact", to: "/contact" },
];

export default function Navbar() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false); // Mobile menu state
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false); // Desktop services dropdown state
  const [isImpactOpen, setIsImpactOpen] = useState(false); // Desktop impact dropdown state
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false); // Mobile services dropdown state
  const [isMobileImpactOpen, setIsMobileImpactOpen] = useState(false); // Mobile impact dropdown state
  const servicesMenuRef = useRef(null);
  const impactMenuRef = useRef(null);

  // Function to determine current page based on location
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/') return 'Home';
    if (path.startsWith('/services')) return 'Services';
    if (path.startsWith('/about')) return 'About Us';
    if (path.startsWith('/impact')) return 'Impact';
    if (path.startsWith('/news')) return 'News';
    if (path.startsWith('/contact')) return 'Contact';
    return 'Home';
  };

  const currentPage = getCurrentPage();

  useEffect(() => {
    function handleClickOutside(event) {
      if (servicesMenuRef.current && !servicesMenuRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
      if (impactMenuRef.current && !impactMenuRef.current.contains(event.target)) {
        setIsImpactOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [servicesMenuRef, impactMenuRef]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white'} border-b border-[#e0e0e0]`}> 
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex-shrink-0">
            <Link to="/" className={`flex items-center transition-all duration-300 ${!isScrolled ? 'md:bg-white md:p-2 md:shadow-sm' : 'p-0'}`}>
              <img 
                src="/images/Milliardi_logo.png" 
                alt="Milliardi Energo Logo" 
                className={`object-contain transition-all duration-300 h-10 ${!isScrolled ? 'md:h-16' : 'md:h-10'}`} 
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {NAV_LINKS.map((link) => (
              <div key={link.name} className="relative" ref={link.subLinks ? (link.name === "Services" ? servicesMenuRef : impactMenuRef) : null}>
                <div className="flex items-center">
                  <Link 
                    to={link.to} 
                    className={`px-3 py-2 rounded-l-full text-sm font-semibold transition-colors duration-300 ${link.subLinks ? 'pr-2' : 'rounded-r-full'} ${
                      currentPage === link.name 
                      ? 'bg-[#e30707] text-white' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {link.name}
                  </Link>
                  {link.subLinks && (
                    <button
                      onClick={() => {
                        if (link.name === "Services") {
                          setIsServicesOpen(prev => !prev);
                          setIsImpactOpen(false);
                        } else if (link.name === "Impact") {
                          setIsImpactOpen(prev => !prev);
                          setIsServicesOpen(false);
                        }
                      }}
                      className={`p-2 rounded-r-full transition-colors duration-300 ${
                        currentPage === link.name 
                        ? 'bg-[#e30707] text-white' 
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <svg className={`w-5 h-5 transition-transform duration-200 ${(link.name === "Services" && isServicesOpen) || (link.name === "Impact" && isImpactOpen) ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                  )}
                </div>
                {/* Desktop Submenu */}
                {link.subLinks && ((link.name === "Services" && isServicesOpen) || (link.name === "Impact" && isImpactOpen)) && (
                  <div className="absolute left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    {link.subLinks.map(subLink => (
                      <Link key={subLink.name} to={subLink.to} className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-[#3bb8ff] rounded-lg">
                        {subLink.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button onClick={() => setIsOpen(!isOpen)} className="p-2 rounded-md text-gray-600 hover:bg-gray-100 z-50 relative">
              {isOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-[70] md:hidden"
          onClick={() => setIsOpen(false)}
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
        />
      )}
      
      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 h-screen w-4/5 max-w-sm shadow-2xl z-[80] transform transition-all duration-300 ease-out md:hidden ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ 
          backgroundColor: '#ffffff',
          background: 'linear-gradient(to bottom, #ffffff, #f9fafb)'
        }}
      >
        <div className="p-6 pt-24 h-full flex flex-col" style={{ backgroundColor: '#ffffff' }}>
          {/* Close Button */}
          <button 
            onClick={() => setIsOpen(false)}
            className="absolute top-6 right-6 p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <nav className="flex flex-col space-y-1 flex-1">
            {NAV_LINKS.map((link) => (
              <div key={link.name}>
                <div className="flex">
                  <Link
                    to={link.to}
                    onClick={() => { if (!link.subLinks) setIsOpen(false); }}
                    className={`flex-grow px-4 py-2.5 text-xs font-medium transition-colors duration-300 ${link.subLinks ? 'rounded-l-lg' : 'rounded-lg'} ${
                        currentPage === link.name ? 'bg-[#e30707] text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {link.name}
                  </Link>
                  {link.subLinks && (
                    <button
                      onClick={() => {
                        if (link.name === "Services") {
                          setIsMobileServicesOpen(!isMobileServicesOpen);
                          setIsMobileImpactOpen(false);
                        } else if (link.name === "Impact") {
                          setIsMobileImpactOpen(!isMobileImpactOpen);
                          setIsMobileServicesOpen(false);
                        }
                      }}
                      className={`px-3 py-2.5 rounded-r-lg text-xs font-medium transition-colors duration-300 ${
                          currentPage === link.name ? 'bg-[#e30707] text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <svg className={`w-4 h-4 transition-transform duration-200 ${(link.name === "Services" && isMobileServicesOpen) || (link.name === "Impact" && isMobileImpactOpen) ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                  )}
                </div>
                {/* Mobile Submenu */}
                {link.subLinks && ((link.name === "Services" && isMobileServicesOpen) || (link.name === "Impact" && isMobileImpactOpen)) && (
                  <div className="pl-4 mt-1 space-y-1">
                    {link.subLinks.map(subLink => (
                      <Link key={subLink.name} to={subLink.to} onClick={() => setIsOpen(false)} className="block px-4 py-2 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-900">
                        {subLink.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
}
